import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { OfferApiService } from '@rd/core/services/offer/offer-api.service';
import {
  FormattedOfferData,
  FormattedPaginatedOfferData,
  HttpError,
  OfferPaginationOptions,
} from '@rd/types';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as actions from '../actions/offer.actions';
import { getOfferListPaginationOptions } from '../selectors/offer.selectors';

@Injectable()
export class OfferEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private offerApiService: OfferApiService,
  ) {}

  loadOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OfferActionTypes.LOAD_OFFERS),
      switchMap((action: actions.LoadOffers) => {
        const { queryParams } = action.payload;
        const source = queryParams?.source;
        return this.offerApiService.getOffers(queryParams).pipe(
          map((formattedOfferResponse: FormattedOfferData) => {
            return new actions.LoadOffersSuccess({
              ...formattedOfferResponse,
              totalPageCount: 1,
              source,
            });
          }),
          catchError((error: HttpError) =>
            of(new actions.LoadOffersFail(error, source)),
          ),
        );
      }),
    ),
  );

  loadOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OfferActionTypes.LOAD_SINGLE_OFFER),
      switchMap((action: actions.LoadSingleOffer) => {
        return this.offerApiService.getOffer(action.payload).pipe(
          map((formatedOfferResponse: FormattedOfferData) => {
            return new actions.LoadOfferSuccess(formatedOfferResponse.offerMap);
          }),
          catchError((error: HttpError) =>
            of(new actions.LoadOfferFail(error)),
          ),
        );
      }),
    ),
  );

  loadPaginatedOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OfferActionTypes.LOAD_PAGINATED_OFFERS),
      switchMap((action: actions.LoadPaginatedOffers) => {
        const { queryParams, paginationOptions } = action.payload;
        const source = queryParams?.source;
        return this.offerApiService
          .getOffersPaginated({
            queryParams,
            page: paginationOptions.page,
            pageSize: paginationOptions.pageSize,
          })
          .pipe(
            map((formattedOfferListResponse: FormattedPaginatedOfferData) => {
              return new actions.LoadOffersSuccess({
                ...formattedOfferListResponse,
                source,
              });
            }),
            catchError((error: HttpError) => {
              return of(new actions.LoadOffersFail(error, source));
            }),
          );
      }),
    ),
  );

  loadBnzHomepageOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OfferActionTypes.LOAD_BNZ_HOMEPAGE_OFFERS),
      switchMap((action: actions.LoadBnzHomepageOffers) => {
        if (!action.payload.isVoxEnabled) {
          return this.loadPartnerOffersForHomepage$().pipe(
            catchError((err) => of(new actions.LoadOffersFail(err))),
          );
        }

        return this.offerApiService
          .getOffersPaginated({
            queryParams: {
              sort: '-score_by_recommended',
              source: 'points_boost',
            },
            page: 1,
            pageSize: 3,
          })
          .pipe(
            switchMap((voxOffers) =>
              voxOffers.offerIds.length > 0
                ? of(
                    new actions.LoadOffersSuccess({
                      ...voxOffers,
                      source: 'points_boost',
                    }),
                  )
                : this.loadPartnerOffersForHomepage$(),
            ),
            catchError(() =>
              this.loadPartnerOffersForHomepage$().pipe(
                catchError((err) => of(new actions.LoadOffersFail(err))),
              ),
            ),
          );
      }),
    ),
  );

  loadMoreOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.OfferActionTypes.LOAD_MORE_OFFERS),
      withLatestFrom(this.store.select(getOfferListPaginationOptions)),
      switchMap(
        ([action, paginationOptions]: [
          actions.LoadMoreOffers,
          OfferPaginationOptions,
        ]) => {
          const { queryParams } = action.payload;
          const source = queryParams?.source;
          return this.offerApiService
            .getOffersPaginated({
              queryParams,
              page: paginationOptions.page,
              pageSize: paginationOptions.pageSize,
            })
            .pipe(
              map((formattedOfferListResponse: FormattedPaginatedOfferData) => {
                return new actions.LoaMoredOffersSuccess({
                  ...formattedOfferListResponse,
                  source,
                });
              }),
              catchError((error: HttpError) => {
                return of(new actions.LoadOfferFail(error));
              }),
            );
        },
      ),
    ),
  );

  private loadPartnerOffersForHomepage$() {
    return this.offerApiService
      .getOffersPaginated({
        queryParams: { sort: '-score_by_recommended', source: 'partner' },
        page: 1,
        pageSize: 3,
      })
      .pipe(
        map(
          (partnerOffers) =>
            new actions.LoadOffersSuccess({
              ...partnerOffers,
              source: 'partner',
            }),
        ),
      );
  }
}
